import { faArrowLeft, faQuestionCircle, faSearch, faTimes } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ErrorBoundary } from '@sentry/remix'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import cn from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import React, { Suspense, useCallback, useContext, useEffect, useState } from 'react'
import { Button, Dialog, Heading, Modal } from 'react-aria-components'

import Tooltip from '~/components/Tooltip'
import { DocumentationPaths, glossary } from '~/docs/_glossary'

interface DocumentationContextType {
  isOpen: boolean
  open: (path?: DocumentationPaths) => void
  close: () => void
  toggle: () => void
  setState: (state: boolean) => void
  activePath?: DocumentationPaths
}

const DocumentationContext = React.createContext<DocumentationContextType | undefined>(undefined)

export function useDocumentation() {
  const ctx = useContext(DocumentationContext)
  if (!ctx) throw new Error('useDocumentation must be used within a DocumentationProvider')
  return ctx
}

export function DocumentationProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [activePath, setActivePath] = useState<DocumentationPaths | undefined>()

  const open = useCallback((path?: DocumentationPaths) => {
    setIsOpen(true)
    setActivePath(path)
  }, [])

  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen((prev) => !prev), [])
  const setState = useCallback((state: boolean) => setIsOpen(state), [])

  return (
    <DocumentationContext.Provider value={{ isOpen, open, close, toggle, setState, activePath }}>
      {children}
    </DocumentationContext.Provider>
  )
}

export function Documentation() {
  const { open, isOpen, setState, close, activePath } = useDocumentation()
  const [searchQuery, setSearchQuery] = useState('')

  // Filter glossary entries based on search query
  const filteredGlossary = Object.entries(glossary)
    .map(([key, entry]) => {
      // Filter headings that match the search query
      const filteredHeadings = entry.headings
        .filter((h) => h.level > 1) // Skip h1 titles
        .filter(
          (h) =>
            searchQuery === '' ||
            h.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
            entry.title.toLowerCase().includes(searchQuery.toLowerCase())
        )

      return { key, entry, filteredHeadings }
    })

    // Only show entries that have matching headings or titles
    .filter(
      ({ entry, filteredHeadings }) =>
        searchQuery === '' ||
        filteredHeadings.length > 0 ||
        entry.title.toLowerCase().includes(searchQuery.toLowerCase())
    )

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[1000] bg-black/50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <Modal isDismissable isOpen={isOpen} onOpenChange={setState}>
            <Dialog>
              {/* Panel */}
              <motion.div
                className="z-[1001] fixed right-0 top-0 h-full w-full md:w-[600px] bg-white shadow-lg outline-none pointer-events-auto overflow-hidden flex flex-col"
                role="dialog"
                aria-modal="true"
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', stiffness: 400, damping: 40 }}
              >
                {/* Header */}
                <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {activePath && (
                      <Button
                        onPress={() => open()}
                        className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-app-blue-base rounded-md"
                      >
                        <FontAwesomeIcon icon={faArrowLeft} />
                      </Button>
                    )}
                    <Heading slot="title" className="text-xl font-semibold text-gray-800">
                      {activePath ? 'Documentation' : 'Rooquest Documentation Library'}
                    </Heading>
                  </div>
                  <Button
                    onPress={close}
                    className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-app-blue-base rounded-md"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  <ErrorBoundary
                    fallback={
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center p-6 bg-red-50 rounded-lg">
                          <p className="text-red-600 font-medium">Failed to load documentation.</p>
                          <button
                            onClick={() => open()}
                            className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                          >
                            Return to documentation index
                          </button>
                        </div>
                      </div>
                    }
                  >
                    {!activePath ?
                      <div className="space-y-4 pb-20">
                        <p className="text-gray-500 border-gray-100">
                          Browse the documentation library or search for a specific topic.
                        </p>

                        {/* Search Bar */}
                        <div className="relative mb-6">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
                          </div>
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search documentation..."
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-full leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-app-blue-base focus:border-app-blue-base sm:text-sm"
                          />
                          {searchQuery && (
                            <button
                              className="absolute inset-y-0 right-0 pr-3 flex items-center"
                              onClick={() => setSearchQuery('')}
                            >
                              <FontAwesomeIcon
                                icon={faTimes}
                                className="text-gray-400 hover:text-gray-600"
                              />
                            </button>
                          )}
                        </div>

                        {filteredGlossary.length === 0 && (
                          <div className="text-center py-6 text-gray-500">
                            No documentation found for "{searchQuery}"
                          </div>
                        )}

                        {filteredGlossary.map(({ key, entry, filteredHeadings }) => (
                          <div key={`documentation-${key}`} className="space-y-1">
                            {/* Top Level Title */}
                            <button
                              className="text-xl font-semibold text-left text-gray-800 hover:text-app-blue-base transition-colors focus:outline-none focus:ring-2 focus:ring-app-blue-base rounded px-2 py-1 -mx-2 w-full cursor-pointer"
                              onClick={() => open(entry.path as DocumentationPaths)}
                            >
                              {entry.title}
                            </button>

                            {/* Sub-Headings */}
                            {filteredHeadings.length > 0 && (
                              <ul className={cn('pl-4 border-l-2 border-gray-200 space-y-1')}>
                                {filteredHeadings.map((heading) => (
                                  <li
                                    key={heading.id}
                                    className={cn(
                                      heading.level === 3 && 'pl-3',
                                      heading.level === 4 && 'pl-6'
                                    )}
                                  >
                                    <button
                                      className="text-sm text-gray-600 hover:text-app-blue-base hover:underline transition-colors focus:outline-none focus:ring-2 focus:ring-app-blue-base rounded px-2 py-1 -mx-2 text-left w-full cursor-pointer"
                                      onClick={() =>
                                        open(`${entry.path}#${heading.id}` as DocumentationPaths)
                                      }
                                    >
                                      {heading.text}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    : <Suspense
                        fallback={
                          <div className="flex items-center justify-center h-64">
                            <div className="animate-pulse text-gray-400">
                              Loading documentation...
                            </div>
                          </div>
                        }
                      >
                        <Content activePath={activePath} />
                      </Suspense>
                    }
                  </ErrorBoundary>
                </div>
              </motion.div>
            </Dialog>
          </Modal>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function Content(props: { activePath: string }) {
  const { open } = useDocumentation()

  const { data, error } = useSuspenseQuery({
    queryKey: ['documentation', props.activePath.split('#')[0]],
    queryFn: async () => {
      if (!props.activePath) return
      const data = await fetch(`/api/documentation?path=${props.activePath}`)
      return data.json()
    },
  })

  // scroll to the heading
  useEffect(() => {
    const heading = document.getElementById(props.activePath.split('#')[1])
    if (heading) {
      heading.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [props.activePath])

  if (error) {
    return (
      <div className="p-6 bg-red-50 rounded-lg text-center">
        <p className="text-red-600 font-medium">Failed to load documentation content.</p>
        <button
          onClick={() => open()}
          className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200"
        >
          Return to documentation index
        </button>
      </div>
    )
  }

  // Render HTML from markdown
  return (
    <div
      className="prose prose-sm prose-slate max-w-none prose-h1:mt-0 prose-h1:text-2xl prose-headings:mt-4 prose-headings:mb-1 prose-p:my-2 prose-ul:my-2 prose-li:my-1 pb-20 
     prose-h2:font-bold prose-h2:pb-1 prose-h2:border-b prose-h2:border-gray-200
     prose-h3:font-semibold prose-h3:text-sm prose-h3:uppercase prose-h3:tracking-wide"
      dangerouslySetInnerHTML={{ __html: data.content }}
    />
  )
}

type DocumentationIconProps = {
  path: DocumentationPaths
  className?: string
}

export function DocumentationIcon({ path, className }: DocumentationIconProps) {
  const { open } = useDocumentation()

  return (
    <button
      onClick={() => open(path)}
      className={cn(
        'shrink-0 text-gray-600 hover:text-app-blue-base transition-colors focus:outline-none focus:ring-2 focus:ring-app-blue-base rounded-full p-1 hover:cursor-pointer',
        className
      )}
      aria-label="Open documentation"
    >
      <Tooltip content="Open documentation">
        <FontAwesomeIcon icon={faQuestionCircle} fixedWidth />
      </Tooltip>
    </button>
  )
}
